import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { ShoppingCart, Menu, User, Package, Search } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { Badge } from '../ui/badge';
import { Logo } from './Logo';

export const Header = () => {
  const { cartCount, user, logout } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
        const data = await response.json();
        setCategories(data || []);
      } catch (error) {
        console.error('Failed to fetch categories');
      }
    };
    fetchCategories();
  }, []);

  return (
    <header className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-border">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <Logo size="default" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {categories.slice(0, 4).map((cat) => (
            <Link 
              key={cat.id}
              to={`/products?category=${cat.id}`} 
              className="text-sm font-medium hover:text-[hsl(var(--primary))] transition-colors"
            >
              {cat.name}
            </Link>
          ))}
          <Link to="/blog" className="text-sm font-medium hover:text-[hsl(var(--primary))] transition-colors">
            Blog
          </Link>
          {categories.length > 4 && (
            <Link to="/products" className="text-sm font-medium text-[hsl(var(--primary))] hover:opacity-80 transition-colors">
              + More
            </Link>
          )}
          {user ? (
            <>
              <Link to="/orders" className="text-sm font-medium hover:text-[hsl(var(--primary))] transition-colors flex items-center gap-1">
                <Package className="w-4 h-4" />
                My Orders
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                Logout
              </Button>
            </>
          ) : null}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/search')}
            data-testid="header-search-button"
          >
            <Search className="w-5 h-5" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => navigate('/cart')}
            data-testid="header-cart-button"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <Badge className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] flex items-center justify-center px-1 text-[10px] font-bold rounded-full bg-red-500 text-white border-2 border-white">
                {cartCount > 99 ? '99+' : cartCount}
              </Badge>
            )}
          </Button>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] overflow-y-auto">
              <div className="flex flex-col gap-4 mt-8 pb-8">
                <div className="mb-2">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Shop by Category</h3>
                  <div className="flex flex-col gap-3">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/products?category=${cat.id}`}
                        className="text-base font-medium hover:text-[hsl(var(--primary))] transition-colors flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-menu-category-${cat.slug || cat.id}`}
                      >
                        <span className="w-1 h-1 rounded-full bg-[hsl(var(--primary))]"></span>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
                
                <div className="pt-3 border-t">
                  <Link
                    to="/blog"
                    className="text-base font-medium hover:text-[hsl(var(--primary))] transition-colors flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-1 h-1 rounded-full bg-[hsl(var(--primary))]"></span>
                    Blog & Tips
                  </Link>
                </div>
                
                {user ? (
                  <div className="pt-4 border-t mt-2">
                    <Link
                      to="/orders"
                      className="text-base font-medium hover:text-[hsl(var(--primary))] transition-colors flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Package className="w-4 h-4" />
                      My Orders
                    </Link>
                    <Button onClick={logout} className="w-full mt-4">
                      Logout
                    </Button>
                  </div>
                ) : null}
                
                <div className="pt-4 border-t mt-4">
                  <Link 
                    to="/admin" 
                    className="text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin Panel
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};